import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// PayPal donation URL
const PAYPAL_URL = 'https://www.paypal.com/ncp/payment/WKSNR77WNYPFS';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'Date unknown';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Date unknown';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return 'Date unknown';
  }
};

/**
 * Normalize story text for safe display.
 *
 * Performs THREE things on the input text:
 *   1. Strips residual <, > characters (defence-in-depth; content_text
 *      should already be plain text but stray markup from LLM paraphrasers
 *      or scrapers can still leak through. Stripping them ensures the
 *      rendered output never contains literal angle brackets).
 *   2. Ensures a space follows every sentence-ending punctuation
 *      (".!?,;:") when the next character is NOT whitespace, a newline,
 *      or a closing tag. This fixes cases where the source text has
 *      "Hello.World" with no space, which otherwise renders run-together.
 *   3. Collapses runs of multiple spaces into one (but preserves newlines
 *      so paragraph breaks survive).
 *
 * Safe to call on already-formatted text — it never deletes content,
 * only adds spaces and removes literal angle brackets.
 */
const normalizeStoryText = (text) => {
  if (!text) return '';
  return String(text)
    // Normalise line endings first
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Strip stray angle brackets (defence-in-depth)
    .replace(/[<>]/g, '')
    // Ensure a space after sentence-ending punctuation when next char is
    // not already whitespace/newline. Uses lookbehind+lookahead so we
    // don't touch punctuation that's already correctly spaced.
    .replace(/([.!?;,:])(?=[^\s\n])/g, '$1 ')
    // Collapse runs of spaces/tabs but preserve newlines
    .replace(/[ \t]{2,}/g, ' ')
    // Collapse 3+ consecutive newlines into 2 (one paragraph break)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

function StoryViewer({ story, onBack, onLikeUpdate, user, login, onDeleteStory }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [userName, setUserName] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(story.like_count || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNameInput, setShowNameInput] = useState(false);

  // Multi-page state
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Related stories state (SEO-friendly internal linking)
  const [relatedStories, setRelatedStories] = useState([]);

  const titleRef = useRef(null);
  const hasScrolledRef = useRef(false);

  const isAdmin = user?.email === 'sandip.kumar.kol@gmail.com';

  // Scroll to the story heading when the story is opened or changes
  useEffect(() => {
    hasScrolledRef.current = false;

    const scrollTimer = setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        hasScrolledRef.current = true;
      }
    }, 100);
    return () => clearTimeout(scrollTimer);
  }, [story?.story_id]);

  // Re-scroll to the title after data loads
  useEffect(() => {
    if (!pageLoading && pages.length > 0 && hasScrolledRef.current) {
      const reScrollTimer = setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.scrollIntoView({
            behavior: 'auto',
            block: 'start'
          });
        }
      }, 50);
      return () => clearTimeout(reScrollTimer);
    }
  }, [pageLoading, pages.length]);

  const handleDeleteStory = async () => {
    if (!window.confirm(`Are you sure you want to delete "${story.title}"? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/admin/stories/${story.story_id}`, { withCredentials: true });
      if (response.data.success) {
        if (onDeleteStory) onDeleteStory(story.story_id);
        if (onBack) onBack();
      } else {
        alert('Failed to delete story: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        if (onDeleteStory) onDeleteStory(story.story_id);
        if (onBack) onBack();
        return;
      }
      console.error('Error deleting story:', error);
      alert('Failed to delete story: ' + (error.response?.data?.error || error.message));
    } finally {
      setDeleting(false);
    }
  };

  // Fetch related stories (same genre, exclude current). Critical for internal linking SEO.
  const fetchRelatedStories = async () => {
    if (!story || !story.genre) {
      setRelatedStories([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/genres/${encodeURIComponent(story.genre)}/stories`);
      if (response.data && response.data.success) {
        const filtered = (response.data.data || [])
          .filter(s => s.story_id !== story.story_id)
          .slice(0, 6);
        setRelatedStories(filtered);
      }
    } catch (error) {
      // Silent: don't disrupt story view
      setRelatedStories([]);
    }
  };

  useEffect(() => {
    if (story && story.story_id) {
      fetchComments();
      if (user) {
        checkIfLiked();
      }
      fetchPages();
      fetchRelatedStories();
    }
  }, [story, user]);

  const fetchPages = async () => {
    setPageLoading(true);
    try {
      const response = await axios.get(`${API_URL}/stories/${story.story_id}/pages`);
      if (response.data.success) {
        setPages(response.data.data.pages);
        setTotalPages(response.data.data.total_pages);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching story pages:', error);
      setPages([{ page_number: 1, content: story.content_text || '', word_count: 0 }]);
      setTotalPages(1);
    } finally {
      setPageLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/stories/${story.story_id}/comments`);
      if (response.data.success) {
        setComments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const checkIfLiked = async () => {
    try {
      const response = await axios.get(`${API_URL}/stories/${story.story_id}/liked`, {
        withCredentials: true
      });
      if (response.data.success) {
        setLiked(response.data.liked);
      }
    } catch (error) {
      console.error('Error checking like status:', error);
      setLiked(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      alert('Please login to like stories');
      login();
      return;
    }

    setLoading(true);
    try {
      if (liked) {
        const response = await axios.delete(`${API_URL}/stories/${story.story_id}/like`, {
          withCredentials: true
        });
        if (response.data.success) {
          setLiked(false);
          setLikeCount(response.data.like_count);
          if (onLikeUpdate) {
            onLikeUpdate({ ...story, like_count: response.data.like_count });
          }
        }
      } else {
        const response = await axios.post(`${API_URL}/stories/${story.story_id}/like`, {}, {
          withCredentials: true
        });
        if (response.data.success) {
          setLiked(true);
          setLikeCount(response.data.like_count);
          if (onLikeUpdate) {
            onLikeUpdate({ ...story, like_count: response.data.like_count });
          }
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      setError(error.response?.data?.error || 'Failed to update like');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      setError('Please enter a comment');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/stories/${story.story_id}/comments`, {
        comment: newComment,
        userName: userName || (user ? user.pet_name : 'Anonymous')
      });

      if (response.data.success) {
        setNewComment('');
        setUserName('');
        setShowNameInput(false);
        await fetchComments();
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      setError('Failed to post comment. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const currentPageData = pages.find(p => p.page_number === currentPage);

  // Build the slug URL for a related story
  const storyHref = (s) => {
    if (s.slug) return `/story/${encodeURIComponent(s.slug)}`;
    if (s.story_id) return `/story/${encodeURIComponent(s.story_id)}`;
    return '#';
  };

  // Resolve the cover image src: prefer base64 (LLM illustrations), fall back to image_path
  // (admin file uploads). Both come from the story row returned by the API.
  const coverSrc = story.cover_image || story.image_path || null;
  // Only use the image if it looks like a valid data URI or an http(s) URL or a root-relative path
  const isSafeImageSrc = (src) => {
    if (!src || typeof src !== 'string') return false;
    if (src.startsWith('data:image/')) return true;
    if (/^https?:\/\//.test(src)) return true;
    if (src.startsWith('/uploads/') || src.startsWith('/images/')) return true;
    return false;
  };
  const safeCoverSrc = isSafeImageSrc(coverSrc) ? coverSrc : null;

  return (
    <div className="story-viewer">
      {/* SEO Meta Tags are emitted by App.js (single source of truth).
          Schema (ShortStory + BreadcrumbList) lives there to avoid duplicates. */}

      <button className="back-btn" onClick={onBack}>← Back</button>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <article className="story-article">
        <h1 ref={titleRef}>{story.title}</h1>

        {/* Cover illustration — rendered right after the title, before meta.
            Falls back to nothing if neither cover_image nor image_path is set. */}
        {safeCoverSrc && (
          <figure className="story-cover">
            <img
              src={safeCoverSrc}
              alt={`${story.title} - cover illustration`}
              loading="lazy"
              decoding="async"
              className="story-cover-img"
            />
          </figure>
        )}

        <div className="story-meta">
          <span>✍️ By {story.author || 'Anonymous'}</span>
          <span>📅 {formatDate(story.created_at || story.publish_date)}</span>
          <span>🏷️ {story.genre || 'Uncategorized'}</span>
          <span>👁️ {story.view_count || 0} views</span>
        </div>

        {/* PayPal Support Button */}
        <div className="paypal-support-banner">
          <p className="paypal-support-text">
            🍽️ <strong>Enjoying this story?</strong> Help us keep the kitchen cooking!
          </p>
          <a
            href={PAYPAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="paypal-support-btn"
          >
            <span className="paypal-btn-icon">🍲</span>
            <span className="paypal-btn-text">Buy us a side dish</span>
            <span className="paypal-btn-subtext">via PayPal</span>
          </a>
        </div>

        {/* Page Navigation (Top) */}
        {totalPages > 1 && (
          <nav className="story-page-nav" aria-label="Story page navigation">
            <button
              className="page-nav-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              ← Previous
            </button>
            <div className="page-indicator">
              Page {currentPage} of {totalPages}
            </div>
            <button
              className="page-nav-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next →
            </button>
          </nav>
        )}

        {/* Page Jump */}
        {totalPages > 1 && (
          <div className="story-page-jump">
            <label htmlFor="page-jump-select">Go to page: </label>
            <select
              id="page-jump-select"
              value={currentPage}
              onChange={(e) => goToPage(parseInt(e.target.value))}
            >
              {pages.map(p => (
                <option key={p.page_number} value={p.page_number}>
                  Page {p.page_number} ({p.word_count} words)
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="story-actions">
          <button
            className={`like-btn ${liked ? 'liked' : ''}`}
            onClick={handleLike}
            disabled={loading}
            aria-label={liked ? `Unlike this story (${likeCount} likes)` : `Like this story (${likeCount} likes)`}
          >
            {liked ? '❤️' : '🤍'} {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
          </button>
          {!user && (
            <span className="login-hint">👉 <button onClick={login} className="login-link">Login</button> to like this story</span>
          )}
          {user && (
            <span className="user-badge">👍 Liking as {user.pet_name}</span>
          )}
          {isAdmin && (
            <button
              className="admin-delete-btn story-delete-btn"
              onClick={handleDeleteStory}
              disabled={deleting}
              aria-label="Delete this story"
            >
              {deleting ? '⏳ Deleting...' : '🗑️ Delete Story'}
            </button>
          )}
        </div>

        {pageLoading ? (
          <div className="page-loading">Loading story pages...</div>
        ) : (
          <div className="story-content">
            {currentPageData ? (
              // Normalise the page content first: strips stray <,>, ensures a
              // space after sentence-ending punctuation, and collapses runs
              // of whitespace. Then split on paragraph breaks and render.
              normalizeStoryText(currentPageData.content).split(/\n\n+/).map((paragraph, i) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return null;

                if (/^[\s\*\-]{3,}$/.test(trimmed) || /^\*\s*\*\s*\*$/.test(trimmed)) {
                  return <p key={i} className="story-paragraph section-break">❦</p>;
                }

                if (trimmed.length < 80 && (
                  /^(chapter|section|part)\s+[\divxlcdm]+/i.test(trimmed) ||
                  /^[\divxlcdm]+\.?\s*$/i.test(trimmed.trim())
                )) {
                  return <p key={i} className="story-paragraph chapter-heading">{trimmed}</p>;
                }

                const startsWithQuote = /^["""\u201C]/.test(trimmed);
                const paraClass = startsWithQuote ? 'story-paragraph dialogue' : 'story-paragraph';

                return <p key={i} className={paraClass}>{trimmed}</p>;
              })
            ) : (
              <div dangerouslySetInnerHTML={{ __html: story.content_html }} />
            )}
          </div>
        )}

        {/* Page Navigation (Bottom) */}
        {totalPages > 1 && (
          <nav className="story-page-nav bottom" aria-label="Story page navigation">
            <button
              className="page-nav-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              ← Previous
            </button>
            <div className="page-indicator">
              Page {currentPage} of {totalPages}
            </div>
            <button
              className="page-nav-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next →
            </button>
          </nav>
        )}

        {/* Progress Bar */}
        {totalPages > 1 && (
          <div className="story-progress" role="progressbar" aria-valuenow={Math.round((currentPage / totalPages) * 100)} aria-valuemin="0" aria-valuemax="100">
            <div
              className="story-progress-bar"
              style={{ width: `${(currentPage / totalPages) * 100}%` }}
            />
            <span className="story-progress-text">
              {((currentPage / totalPages) * 100).toFixed(0)}% complete
            </span>
          </div>
        )}

        {/* Related Stories - Internal Linking for SEO */}
        {relatedStories.length > 0 && (
          <section className="related-stories" aria-label={`More ${story.genre || ''} stories`}>
            <h3>📚 More {story.genre || 'Stories'} You Might Like</h3>
            <div className="related-stories-grid">
              {relatedStories.map(s => (
                <Link
                  key={s.story_id || s.id}
                  to={storyHref(s)}
                  className="related-story-card"
                  title={`${s.title} by ${s.author || 'Anonymous'}`}
                >
                  <h4>{s.title || 'Untitled'}</h4>
                  <p>{s.author || 'Anonymous'}</p>
                  <span className="related-story-genre">{s.genre || 'Fiction'}</span>
                </Link>
              ))}
            </div>
            <p className="related-stories-more">
              <Link to={`/genre/${encodeURIComponent(story.genre)}`}>
                See all {story.genre} stories →
              </Link>
            </p>
          </section>
        )}

        <section className="comments-section" aria-label="Comments">
          <h3>💬 Comments ({comments.length})</h3>

          <form onSubmit={handleComment} className="comment-form">
            {!user && !showNameInput && (
              <div className="comment-options">
                <button type="button" onClick={() => setShowNameInput(true)}>
                  💬 Comment as Anonymous
                </button>
              </div>
            )}

            {(showNameInput || user) && (
              <>
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="comment-name"
                  disabled={!!user}
                  aria-label="Your name"
                />
                {user && (
                  <div className="commenting-as">
                    💬 Commenting as <strong>{user.pet_name}</strong>
                  </div>
                )}
                <textarea
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows="3"
                  disabled={loading}
                  aria-label="Comment text"
                />
                <button type="submit" disabled={loading}>
                  {loading ? 'Posting...' : '📝 Post Comment'}
                </button>
              </>
            )}
          </form>

          <div className="comments-list">
            {comments.length > 0 ? (
              comments.map(comment => (
                <div key={comment.id} className="comment">
                  <div className="comment-header">
                    <strong>{comment.user_name || 'Anonymous'}</strong>
                    <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                  </div>
                  <p>{comment.comment_text}</p>
                </div>
              ))
            ) : (
              <p className="no-comments">No comments yet. Be the first to comment!</p>
            )}
          </div>
        </section>
      </article>
    </div>
  );
}

export default StoryViewer;